/**
 * @file FocalPointPicker.tsx
 * @description Мини-фото с точкой фокуса (phase19.16 Р2; uiux-design-r2 §2
 * «[мини-фото ⊕фокус]»): тап по картинке ставит точку, которая останется в
 * кадре при любой ширине экрана. Клавиатура — стрелки двигают точку на 5 %.
 *
 * SB120 этап 1 (uiux-sb120-stage1 §2.3): `frame` — рамка-подсказка «░ уйдёт
 * за край»: при известных пропорциях файла мини-фото показывается целиком,
 * а то, что на этом экране срежется (`object-fit: cover` по пропорции места
 * и масштабу кадра), затемнено.
 */

import type { KeyboardEvent, MouseEvent } from "react";

import { contentMediaUrl } from "../../../../lib/api-content-media";
import { visibleRect } from "../../../../lib/site-builder/look-size";

interface Focus {
  x: number;
  y: number;
}

interface Props {
  mediaId: string;
  value: Focus;
  onChange: (focus: Focus) => void;
  alt?: string;
  /** Размер превью (по умолчанию — широкая полоса; фото категории — квадрат 160). */
  className?: string;
  /** Рамка места: пропорции места, файла (null — неизвестны, рамки нет) и масштаб кадра. */
  frame?: { aspect: number; photoAspect: number | null; zoom?: number };
}

const clamp = (v: number) => Math.round(Math.min(100, Math.max(0, v)));

export function FocalPointPicker({ mediaId, value, onChange, alt = "", className = "h-32 w-full max-w-72", frame }: Props) {
  const photoAspect = frame?.photoAspect ?? null;
  const visible = frame && photoAspect ? visibleRect(value, { ...frame, photoAspect }) : null;
  function onClick(event: MouseEvent<HTMLButtonElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    onChange({ x: clamp(((event.clientX - rect.left) / rect.width) * 100), y: clamp(((event.clientY - rect.top) / rect.height) * 100) });
  }
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const step: Record<string, [number, number]> = { ArrowLeft: [-5, 0], ArrowRight: [5, 0], ArrowUp: [0, -5], ArrowDown: [0, 5] };
    const d = step[event.key];
    if (!d) return;
    event.preventDefault();
    onChange({ x: clamp(value.x + d[0]), y: clamp(value.y + d[1]) });
  }
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={onClick}
        onKeyDown={onKeyDown}
        aria-label={`Точка фокуса фото: ${value.x} % слева, ${value.y} % сверху. Нажмите на фото, чтобы переставить`}
        className={`relative ${visible ? "w-full max-w-72" : className} overflow-hidden rounded-xl bg-surface-low outline-none focus-visible:ring-2 focus-visible:ring-ring`}
        style={visible && photoAspect ? { aspectRatio: String(photoAspect) } : undefined}
      >
        <img src={contentMediaUrl(mediaId)} alt={alt} className="h-full w-full object-cover" draggable={false} />
        {visible ? (
          <span
            aria-hidden
            data-slot="frame-hint"
            className="pointer-events-none absolute rounded-sm border border-white/80 [box-shadow:0_0_0_9999px_rgb(0_0_0/0.45)]"
            style={{ left: `${visible.left * 100}%`, top: `${visible.top * 100}%`, width: `${visible.width * 100}%`, height: `${visible.height * 100}%` }}
          />
        ) : null}
        <span
          aria-hidden
          className="pointer-events-none absolute size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary/60 shadow"
          style={{ left: `${value.x}%`, top: `${value.y}%` }}
        />
      </button>
      <span className="type-caption text-on-surface-variant">
        Нажмите на фото — эта точка останется в кадре на любом экране.{visible ? " Затемнённое уйдёт за край на этом экране." : ""}
      </span>
    </div>
  );
}
